/**
 * QR Code Service
 * Handles QR code generation and scanning operations
 */

import { QRCodeConfiguration, EncodingOperation, EncoderToolType, ErrorCorrectionLevel } from '../types';
import { ProcessingError, ValidationError, FileError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';
import QRCode from 'qrcode';
import { scanQRCodeFromFile, scanQRCodeFromUrl, isQRScanningSupported } from '../utils/qrScannerUtil';

export class QRCodeService {
  /**
   * Generate a QR code from text data
   */
  static async generate(config: QRCodeConfiguration): Promise<EncodingOperation> {
    try {
      // Validate configuration using validateConfiguration
      const validation = this.validateConfiguration(config);

      if (!validation.isValid) {
        throw new ValidationError(
          validation.errors.join(', '),
          EncoderToolType.QR_CODE,
          'Please check your QR code configuration'
        );
      }

      // Simulate QR code generation (in production, would use qrcode.react)
      const qrCodeData = await this.generateQRCodeData(config);
      
      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.QR_CODE,
        operationType: 'encode',
        inputData: config.text,
        outputData: qrCodeData,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          size: config.size,
          errorCorrectionLevel: config.errorCorrectionLevel,
          exportFormat: config.exportFormat,
          textLength: config.text.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }
      
      throw new ProcessingError(
        'Failed to generate QR code',
        EncoderToolType.QR_CODE,
        'Please check your text and QR code settings'
      );
    }
  }

  /**
   * Scan QR code from image file
   */
  static async scan(imageFile?: File, imageUrl?: string): Promise<EncodingOperation> {
    try {
      let inputSource: string | File;
      
      if (imageFile) {
        // Validate image file
        const validation = CommonValidator.validateFile(imageFile, 
          CommonValidator.getValidationRules(EncoderToolType.QR_CODE)
        );
        
        if (!validation.isValid) {
          throw new ValidationError(validation.error!, EncoderToolType.QR_CODE, validation.suggestion);
        }
        inputSource = imageFile;
      } else if (imageUrl) {
        inputSource = imageUrl;
        // Validate URL
        try {
          new URL(imageUrl);
        } catch {
          throw new ValidationError(
            'Invalid image URL',
            EncoderToolType.QR_CODE,
            'Please provide a valid image URL'
          );
        }
      } else {
        throw new ValidationError(
          'No image provided for scanning',
          EncoderToolType.QR_CODE,
          'Please provide an image file or URL'
        );
      }

      // Simulate QR code scanning (in production, would use @yudiel/react-qr-scanner)
      const scannedText = await this.scanQRCodeFromImage(inputSource);
      
      if (!scannedText) {
        throw new ProcessingError(
          'No QR code found in image',
          EncoderToolType.QR_CODE,
          'Ensure the image contains a clear QR code'
        );
      }

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.QR_CODE,
        operationType: 'decode',
        inputData: inputSource,
        outputData: scannedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          scanResult: scannedText,
          scanMethod: imageFile ? 'file' : 'url'
        }
      };
    } catch (error) {
      if (error instanceof ValidationError || error instanceof FileError) {
        throw error;
      }
      
      throw new ProcessingError(
        'Failed to scan QR code',
        EncoderToolType.QR_CODE,
        'Please check the image and try again'
      );
    }
  }

  /**
   * Create QR code configuration
   */
  static createConfiguration(
    text: string,
    options: {
      size?: number;
      errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
      exportFormat?: 'PNG' | 'SVG' | 'JPG';
      includeLogo?: boolean;
      logoData?: string;
    } = {}
  ): QRCodeConfiguration {
    return {
      id: this.generateOperationId(),
      text,
      size: options.size || 256,
      errorCorrectionLevel: options.errorCorrectionLevel || 'M',
      exportFormat: options.exportFormat || 'PNG',
      includeLogo: options.includeLogo || false,
      logoData: options.logoData,
      generatedAt: new Date()
    };
  }

  /**
   * Validate QR code configuration
   */
  static validateConfiguration(config: QRCodeConfiguration): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.text) {
      errors.push('Text is required');
    }

    if (config.size < 100 || config.size > 10000) {
      errors.push('size must be between 100 and 10000');
    }

    if (config.text.length > 4296) {
      errors.push('Text is too long');
    }

    const validErrorLevels: ErrorCorrectionLevel[] = ['L', 'M', 'Q', 'H'];
    if (!validErrorLevels.includes(config.errorCorrectionLevel)) {
      errors.push('Invalid error correction level');
    }

    const validFormats: ('PNG' | 'SVG' | 'JPG')[] = ['PNG', 'SVG', 'JPG'];
    if (!validFormats.includes(config.exportFormat)) {
      errors.push('Invalid export format');
    }

    if (config.includeLogo && !config.logoData) {
      errors.push('Logo data is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get supported export formats
   */
  static getSupportedExportFormats(): ('PNG' | 'SVG' | 'JPG')[] {
    return ['PNG', 'SVG', 'JPG'];
  }

  /**
   * Get supported error correction levels
   */
  static getSupportedErrorCorrectionLevels(): { level: 'L' | 'M' | 'Q' | 'H'; description: string }[] {
    return [
      { level: 'L', description: 'Low (~7% correction capacity)' },
      { level: 'M', description: 'Medium (~15% correction capacity)' },
      { level: 'Q', description: 'Quartile (~25% correction capacity)' },
      { level: 'H', description: 'High (~30% capacity)' }
    ];
  }

  
  /**
   * Generate real QR code data using the qrcode library
   */
  private static async generateQRCodeData(config: QRCodeConfiguration): Promise<string> {
    try {
      const qrOptions = {
        width: config.size,
        margin: 1,
        color: {
          dark: '#000000',  // Black dots
          light: '#FFFFFF'  // White background
        },
        errorCorrectionLevel: config.errorCorrectionLevel as 'L' | 'M' | 'Q' | 'H'
      };

      if (config.exportFormat === 'SVG') {
        // Generate SVG QR code
        const svgString = await QRCode.toString(config.text, {
          ...qrOptions,
          type: 'svg'
        });
        return svgString;
      } else {
        // Generate PNG/JPG QR code as Base64 data URL
        const dataUrl = await QRCode.toDataURL(config.text, qrOptions);

        if (config.exportFormat === 'JPG') {
          // For JPG, convert PNG data URL to simulate JPG (qrcode library doesn't support JPG directly)
          // In production, you might want to use canvas to convert to actual JPG
          return dataUrl.replace('image/png', 'image/jpeg');
        }

        return dataUrl; // PNG format
      }
    } catch (error) {
      throw new ProcessingError(
        `Failed to generate QR code: ${error instanceof Error ? error.message : 'Unknown error'}`,
        EncoderToolType.QR_CODE,
        'Try with shorter text or different settings'
      );
    }
  }

  /**
   * Scan QR code from image using real QR code scanning
   */
  private static async scanQRCodeFromImage(input: string | File): Promise<string | null> {
    // Check if QR scanning is supported (browser environment)
    if (!isQRScanningSupported()) {
      throw new ProcessingError(
        'QR code scanning is only supported in browser environments',
        EncoderToolType.QR_CODE,
        'Use this feature in a web browser'
      );
    }

    try {
      let scanResult: string | null;

      if (typeof input === 'string') {
        // For URL input, scan from URL
        scanResult = await scanQRCodeFromUrl(input);
      } else {
        // For file input, scan from file
        scanResult = await scanQRCodeFromFile(input);
      }

      return scanResult;
    } catch (error) {
      throw new ProcessingError(
        `Failed to scan QR code: ${error instanceof Error ? error.message : 'Unknown error'}`,
        EncoderToolType.QR_CODE,
        'Ensure the image contains a clear, well-lit QR code and is a valid image file'
      );
    }
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `qr-code-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}